
import axios from 'axios';
import { BaseApiUrl } from './base';

const base = BaseApiUrl;

// ============================================
// 放学大屏设备管理
// ============================================

/**
 * 获取大屏设备列表（分页）
 * @param {Object} params { schId, screenName, page, intPageSize }
 */
export const getScreenList = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetScreenList`, { params: params });
};

/**
 * 解析 GetScreenList 响应中的列表（兼容 PageModel 与旧版数组）
 * @param {Object} res axios 响应
 * @returns {Array}
 */
export const parseScreenListResponse = (res) => {
  const response = res && res.data && res.data.response;
  if (!response) return [];
  if (Array.isArray(response)) return response;
  return response.data || [];
};

/**
 * 保存大屏设备（新增/修改）
 * @param {Object} params ScreenEditInput
 */
export const saveScreen = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveScreen`, params);
};

/**
 * 删除大屏设备
 * @param {Object} params { id }
 */
export const deleteScreen = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/DeleteScreen?id=${params.id}`);
};

// ============================================
// 校门 LED 叫号设备管理（创建于 2026-09-30）
// ============================================

/**
 * 获取校门 LED 设备列表（分页）
 * @param {Object} params { departmentId, campusId, keyword, page, intPageSize }
 */
export const getLedDeviceList = (params) => {
  return axios.get(`${base}/api/DismissalLed/GetLedDeviceList`, { params });
};

/**
 * 保存校门 LED 设备（新增/修改）
 * @param {Object} params LedDeviceEditInput
 */
export const saveLedDevice = (params) => {
  return axios.post(`${base}/api/DismissalLed/SaveLedDevice`, params);
};

/**
 * 删除校门 LED 设备
 * @param {Object} params { id }
 */
export const deleteLedDevice = (params) => {
  return axios.post(`${base}/api/DismissalLed/DeleteLedDevice?id=${params.id}`);
};

/**
 * 向单台校门 LED 下发测试指令（文字、语音或切换节目）
 * @param {Object} params { Id, DepartmentId, Mode, Text, ProgramNo }
 */
export const sendLedCommand = (params) => {
  return axios.post(`${base}/api/DismissalLed/SendLedCommand`, params);
};

// ============================================
// 放学时间配置管理
// ============================================

/**
 * 获取放学配置列表（分页）
 * @param {Object} params { schId, gradeName, page, intPageSize }
 */
export const getConfigList = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetConfigList`, { params: params });
};

/**
 * 保存放学配置
 * @param {Object} params ConfigEditInput
 */
export const saveConfig = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveConfig`, params);
};

/**
 * 删除放学配置
 * @param {Object} params { id }
 */
export const deleteConfig = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/DeleteConfig?id=${params.id}`);
};


// ============================================
// 放学标签（校区维度，扁平）
// ============================================

/**
 * 获取校区下标签项列表（含选项值，分页）
 * @param {Object} params { campusId, page?, intPageSize? }
 */
export const getDismissalTagCategories = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalTagCategories`, { params });
};

/**
 * 获取校区下全部标签选项（扁平）
 * @param {Object} params { campusId }
 */
export const getDismissalTags = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalTags`, { params });
};

/**
 * 保存标签项
 * @param {Object} body SaveDismissalTagCategoryInput
 */
export const saveDismissalTagCategory = (body) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveDismissalTagCategory`, body);
};

/**
 * 删除标签项
 * @param {Object} params { campusId, categoryName }
 */
export const deleteDismissalTagCategory = (params) => {
  return axios.post(
    `${base}/api/DismissalBigScreen/DeleteDismissalTagCategory?campusId=${params.campusId}&categoryName=${encodeURIComponent(params.categoryName || '')}`
  );
};

// ============================================
// 学生放学标签
// ============================================

/**
 * 按机构范围分页查询学生及标签
 * @param {Object} params { scopeDeptOrgType, scopeId, campusId?, cardNumber?, studentName?, liveTypeId?, tagId?, page?, intPageSize? }
 */
export const getStudentDismissalTagQuery = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetStudentDismissalTagQuery`, { params });
};

/**
 * 按学生 Id 批量查询标签绑定（选人 enrich，避免分页截断导致计数不准）。创建于 2026-11-02
 * @param {Object} body { studentIds: number[], campusId?: number }
 */
export const getStudentDismissalTagsByStudentIds = (body) => {
  return axios.post(`${base}/api/DismissalBigScreen/GetStudentDismissalTagsByStudentIds`, body || {});
};

/**
 * 保存单名学生多个标签
 * @param {Object} body { classId, studentId, tagIds: number[] }
 */
export const saveStudentDismissalTag = (body) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveStudentDismissalTag`, body);
};

/**
 * 导出学生放学标签 Excel（须 campusId；范围：班级 > 年级 > 学校）
 * @param {Object} params { campusId, schoolId?, gradeId?, classId? }
 */
export const exportStudentDismissalTags = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/ExportStudentDismissalTags`, {
    params,
    responseType: 'blob'
  });
};

/**
 * 导入学生放学标签（multipart，字段名 file；query: campusId）
 * @param {FormData} formData
 * @param {number} campusId
 */
export const importStudentDismissalTags = (formData, campusId) => {
  return axios.post(
    `${base}/api/DismissalBigScreen/ImportStudentDismissalTags?campusId=${campusId}`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } }
  );
};

// ============================================
// 放学大屏配置（违停审核 / 投放 / 分屏 / 时长）
// ============================================

/**
 * 获取放学大屏配置
 * @param {Object} params { departmentId }
 */
export const getDismissalScreenConfig = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalScreenConfig`, { params });
};

/**
 * 保存放学大屏配置
 * @param {Object} params { departmentId, needApproval, parentDingNotifyEnabled }
 */
export const setDismissalScreenConfig = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/SetDismissalScreenConfig`, params);
};

// ============================================
// 放学后台查询
// ============================================

/**
 * 查询组织节点下学生及放学管理员状态，支持分页和关键词
 * @param {Object} params { departmentId, page, intPageSize, onlyAdmin, keyword }
 */
export const getDismissalPersonnelAdmin = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalPersonnelAdmin`, { params });
};

/** 导出组织节点下的放学管理员班级统计。 */
export const exportDismissalPersonnelAdmin = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/ExportDismissalPersonnelAdmin`, {
    params,
    responseType: 'blob'
  });
};

/** 立即设置单个学生的放学管理员状态。 */
export const setDismissalPersonnelAdmin = (data) => {
  return axios.post(`${base}/api/DismissalBigScreen/SetDismissalPersonnelAdmin`, data);
};

/**
 * 后台保存班级放学管理员
 * @param {Object} body { classId, studentIds }
 */
export const saveDismissalPersonnelAdmin = (body) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveDismissalPersonnelAdmin`, body);
};

/**
 * 分页查询放学事件
 * @param {Object} params { departmentId, startDate, endDate, operatorName, operatorRole, periodName, page, intPageSize }
 */
export const getDismissalEventAdminList = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalEventAdminList`, { params });
};

/**
 * 按部门范围汇总未点击放学班级
 * @param {Object} params { departmentId, startDate, endDate, periodName }
 */
export const getDismissalEventScopeSummary = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalEventScopeSummary`, { params });
};

/**
 * 按部门范围汇总某一天未点击放学的班级
 * @param {Object} params { departmentId, date, periodName }
 */
export const getDismissalDailySummary = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalDailySummary`, { params });
};

/**
 * 导出某一天未点击放学的班级
 * @param {Object} params { departmentId, date, periodName }
 */
export const exportDismissalDailySummary = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/ExportDismissalDailySummary`, {
    params,
    responseType: 'blob'
  });
};

/**
 * 按日期范围汇总未点击放学的班级，含整段和每一天
 * @param {Object} params { departmentId, startDate, endDate, periodName }
 */
export const getDismissalRangeSummary = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalRangeSummary`, { params });
};

/**
 * 导出日期范围内的放学汇总
 * @param {Object} params { departmentId, startDate, endDate, periodName }
 */
export const exportDismissalRangeSummary = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/ExportDismissalRangeSummary`, {
    params,
    responseType: 'blob'
  });
};

/**
 * 查询放学事件详情
 * @param {Object} params { eventId, studentPage, studentPageSize, studentKeyword }
 */
export const getDismissalEventAdminDetail = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetDismissalEventAdminDetail`, { params });
};

/**
 * 违停点评扣分级联树
 * @param {Object} params { departmentId }
 */
export const getViolationEvalDeductTree = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetViolationEvalDeductTree`, { params });
};

/**
 * 违停值日扣分级联树（按校区模板，无一级类回退学校通用）
 * @param {Object} params { departmentId, campusId }
 */
export const getViolationDutyDeductTree = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetViolationDutyDeductTree`, { params });
};

// ============================================
// 违停内容审核人
// ============================================

/**
 * 获取审核人列表
 */
export const getScreenContentApproverList = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetScreenContentApproverList`, { params });
};

/**
 * 保存审核人（新增/编辑）
 */
export const saveScreenContentApprover = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveScreenContentApprover`, params);
};

/**
 * 删除审核人
 */
export const deleteScreenContentApprover = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/DeleteScreenContentApprover?id=${params.id}`);
};

// ============================================
// 大屏展示内容
// ============================================

/**
 * 获取大屏展示内容列表
 */
export const getScreenContentList = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/GetScreenContentList`, { params });
};

/**
 * 违停：按车牌在监护人车牌库中反查当前机构下学生（0~N 条）
 * @param {Object} params { schId, plate }
 */
export const lookupStudentsByPlateForViolation = (params) => {
  return axios.get(`${base}/api/DismissalBigScreen/LookupStudentsByPlateForViolation`, { params });
};

/**
 * 保存大屏展示内容
 */
export const saveScreenContent = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/SaveScreenContent`, params);
};

/**
 * 删除大屏展示内容
 */
export const deleteScreenContent = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/DeleteScreenContent?id=${params.id}`);
};

/**
 * 审核大屏展示内容
 */
export const auditScreenContent = (params) => {
  return axios.post(`${base}/api/DismissalBigScreen/AuditScreenContent`, params);
};

/**
 * 获取放学大屏中转页数据（自动切换）
 */
export const getDismissalBigScreenAutoData = (params) => {
  return axios.get(`${base}/api/BigScreen/GetDismissalBigScreenAutoData`, { params });
};

